/*
  Slideshow background videos.

  - Loads exactly one rendition per <video> (mobile or desktop) based on the
    viewport at load time, using the data-src-* attributes.
  - Plays only while the slideshow is in the viewport and only on the active
    slide (Swiper toggles .swiper-slide-active; a MutationObserver watches it).
    Background tabs need no special handling: IntersectionObserver does not
    fire there and browsers throttle muted media themselves.
  - Respects prefers-reduced-motion: the poster image stays, nothing plays.
  - Adds .is-playing once frames are rendering so the video fades in over the
    poster instead of flashing black.
*/
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 719px)';
  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pickSource(video) {
    var isMobile = window.matchMedia(MOBILE_QUERY).matches;
    var mobile = video.dataset.srcMobile;
    var desktop = video.dataset.srcDesktop;
    if (isMobile) return mobile || desktop || '';
    return desktop || '';
  }

  function prepare(video) {
    if (video.dataset.prepared) return;
    var src = pickSource(video);
    if (!src) {
      video.dataset.prepared = 'empty';
      return;
    }
    video.src = src;
    video.dataset.prepared = 'true';
    video.addEventListener('playing', function () {
      video.classList.add('is-playing');
    });
  }

  function play(video) {
    if (REDUCED_MOTION) return;
    prepare(video);
    if (video.dataset.prepared !== 'true') return;
    if (!video.paused) return;
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* Autoplay blocked: the poster image stays visible. */
      });
    }
  }

  function pause(video) {
    if (!video.paused) video.pause();
  }

  function initSection(section) {
    if (section.dataset.videoInit) return;
    section.dataset.videoInit = 'true';

    var videos = Array.prototype.slice.call(section.querySelectorAll('[data-slideshow-video]'));
    if (!videos.length) return;

    var inView = false;

    function isActive(video) {
      var slide = video.closest('.swiper-slide');
      if (!slide) return true;
      // Before Swiper initialises (or with a single slide) no slide carries the class: treat the first as active.
      var anyActive = section.querySelector('.swiper-slide-active');
      if (!anyActive) return slide === section.querySelector('.swiper-slide');
      return slide.classList.contains('swiper-slide-active');
    }

    function sync() {
      videos.forEach(function (video) {
        if (inView && isActive(video)) {
          play(video);
        } else {
          pause(video);
        }
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          inView = entry.isIntersecting;
        });
        sync();
      }, { threshold: 0.15 });
      io.observe(section);
    } else {
      inView = true;
      sync();
    }

    var slides = section.querySelectorAll('.swiper-slide');
    if (slides.length && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () { sync(); });
      slides.forEach(function (slide) {
        mo.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });
      // Swiper (loop mode) may clone slides after init: watch the wrapper for new children too.
      var wrapper = section.querySelector('.swiper-wrapper');
      if (wrapper) {
        new MutationObserver(function () {
          videos = Array.prototype.slice.call(section.querySelectorAll('[data-slideshow-video]'));
          section.querySelectorAll('.swiper-slide').forEach(function (slide) {
            mo.observe(slide, { attributes: true, attributeFilter: ['class'] });
          });
          sync();
        }).observe(wrapper, { childList: true });
      }
    }

  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-section-type="slideshow"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: sections are re-rendered on change.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
